import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ActionPreviewTableSection } from '../../../../../services/gable/gable.service';

/**
 * Generic `table` section — a column-headed grid of rows, used by any action
 * whose preview lists line items (journal entry debits and credits today).
 */
@Component({
    selector: 'app-ap-table-section',
    templateUrl: './table-section.component.html',
    styleUrls: ['./table-section.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActionPreviewTableSectionComponent {
    @Input({ required: true }) section!: ActionPreviewTableSection;

    get columns(): string[] {
        return (this.section.columns ?? []).map((column) => this.text(column));
    }

    get tableColumns(): { key: string; header: string; type: 'text'; mobile?: 'title' | 'meta' }[] {
        const columns = this.columns;
        const allOnFace = columns.length <= 4;
        return columns.map((header, i) => ({
            key: 'c' + i,
            header,
            type: 'text',
            ...(allOnFace ? { mobile: i === 0 ? 'title' : 'meta' } : {}),
        }));
    }

    get tableRows(): Record<string, string | number>[] {
        return this.rows.map((row, r) => {
            const out: Record<string, string | number> = { id: r };
            row.forEach((cell, i) => (out['c' + i] = cell));
            return out;
        });
    }

    get rows(): string[][] {
        return (this.section.rows ?? []).map((row) => this.cells(row));
    }

    trackByIndex(index: number): number {
        return index;
    }

    private cells(row: unknown): string[] {
        if (Array.isArray(row)) {
            return row.map((cell) => this.text(cell));
        }

        if (row !== null && typeof row === 'object') {
            return this.section.columns.map((column) => this.text((row as Record<string, unknown>)[this.text(column)]));
        }

        return [this.text(row)];
    }

    private text(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return typeof value === 'string' ? value : String(value);
    }
}
